import { createSlice } from '@reduxjs/toolkit';
import { PREFERRED_COMMUNICATION_METHOD } from 'config/app-config';
import type { EmailVerificationResponse } from 'api/models/TEmail';
import type { RootState } from 'state/store';
import type { NormalizedError } from 'errors/types';

export type AccountDetails = {
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  birthDate: string | null;
  email: string | null;
  phoneNumber: string | null;
  phoneType: string | null;
  isChangeOfTenancy: boolean | null;
  isChangeOfName: boolean;
  relationshipToProperty: string | null;
  preferredCommunicationMethod:
    | typeof PREFERRED_COMMUNICATION_METHOD[keyof typeof PREFERRED_COMMUNICATION_METHOD]
    | null;
};

export type AccountState = {
  isLoading: boolean;
  errors: null | NormalizedError[];
  details: AccountDetails;
  data: EmailVerificationResponse;
};

export const initialState: AccountState = {
  isLoading: false,
  errors: null,
  details: {
    title: null,
    firstName: null,
    lastName: null,
    birthDate: null,
    email: null,
    phoneNumber: null,
    phoneType: null,
    isChangeOfTenancy: null,
    isChangeOfName: false,
    relationshipToProperty: null,
    preferredCommunicationMethod: PREFERRED_COMMUNICATION_METHOD.EMAIL
  },
  data: {}
};

export const slice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setInitialState: (state) => {
      Object.entries(initialState).forEach(
        ([key, value]) => (state[key] = value)
      );
    },
    setIsLoading: (state) => {
      state.isLoading = true;
      state.errors = initialState.errors;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.data = payload;
      state.errors = initialState.errors;
    },

    setError: (state, { payload }) => {
      state.isLoading = false;
      state.data = {};
      state.errors = payload;
    },
    setAccountField: (state, { payload: { name, value } }) => {
      state.details[name] = value;
    }
  }
});

export const {
  setInitialState,
  setAccountField,
  setSuccess,
  setError,
  setIsLoading
} = slice.actions;

export const selectSlice = (state: RootState) => state[slice.name];
export const selectDetails = (state: RootState) => selectSlice(state).details;
export const selectIsLoading = (state: RootState) =>
  selectSlice(state).isLoading;
export const selectErrors = (state: RootState) => selectSlice(state).errors;
export const selectData = (state: RootState) => selectSlice(state).data;

export default slice.reducer;
